import React from 'react';

import {humanEnumeration} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import type {Character} from '../model/character';
import {abilityLabel, conditionLabel, damageTypeLabel, senseLabel} from '../model/labels';
import {EXTRA_SPEEDS, ExtraSpeed, SENSES} from '../model/types';
import {formatFeet} from './format';
import {Fact, SheetBlock} from './sheet-block';

import css from './sheet.module.css';

const SPEED_LABELS: Record<ExtraSpeed, string> = {
	fly: 'Полёт',
	swim: 'Плавание',
	climb: 'Лазание',
	burrow: 'Копание',
};

type FactEntry = [label: string, value: string];

const senseFacts = ({senses, speeds, passivePerception}: Character): FactEntry[] => [
	['Пассивное Внимание', String(passivePerception)],
	...SENSES.filter((sense) => senses[sense]).map(
		(sense): FactEntry => [senseLabel(sense), formatFeet(senses[sense]!)],
	),
	...EXTRA_SPEEDS.filter((speed) => speeds[speed]).map(
		(speed): FactEntry => [`Скорость: ${SPEED_LABELS[speed].toLowerCase()}`, formatFeet(speeds[speed]!)],
	),
];

type DefenseEffect = Parameters<Character['defenseSources']>[0];

const DEFENSES: [label: string, effect: DefenseEffect, format: (value: string) => string][] = [
	['Устойчивость', 'resistances', damageTypeLabel],
	['Невосприимчивость', 'immunities', damageTypeLabel],
	['Уязвимость', 'vulnerabilities', damageTypeLabel],
	['Невосприимчивость к состояниям', 'conditionImmunities', conditionLabel],
	['Преимущество в испытаниях', 'savingThrowAdvantages', abilityLabel],
	['Преимущество против состояний', 'conditionAdvantages', conditionLabel],
];

const DEFENSE_VALUES: Record<DefenseEffect, (character: Character) => string[]> = {
	resistances: (character) => character.resistances,
	immunities: (character) => character.immunities,
	vulnerabilities: (character) => character.vulnerabilities,
	conditionImmunities: (character) => character.conditionImmunities,
	savingThrowAdvantages: (character) => character.savingThrowAdvantages,
	conditionAdvantages: (character) => character.conditionAdvantages,
};

/** устойчивости, невосприимчивости и преимущества с источником в скобках: только заполненные */
export const defenseFacts = (character: Character): FactEntry[] =>
	DEFENSES.map(([label, effect, format]): FactEntry => {
		const values = DEFENSE_VALUES[effect](character).map((value) => {
			const sources = character.defenseSources(effect, value);
			return sources.length ? `${format(value)} (${sources.join(', ')})` : format(value);
		});
		return [label, humanEnumeration(values)];
	}).filter(([, value]) => value);

export const FactList: React.FC<{facts: FactEntry[]}> = ({facts}) => (
	<dl className={css.facts}>
		{facts.map(([label, value]) => (
			<Fact key={label} label={label}>
				{value}
			</Fact>
		))}
	</dl>
);

export const SensesBlock: React.FC = () => {
	const {character} = useCharacter();
	return (
		<SheetBlock title="Восприятие" card>
			<FactList facts={senseFacts(character)} />
		</SheetBlock>
	);
};
